import { ReviewMask } from '../../../sdk/@types/Revisao';

/**
 * Desfazer/refazer da lista de marcações, por cópias da lista inteira — não
 * por diffs nem por comandos invertíveis (design.md, Decisão 6: a lista é
 * pequena, unidades, não milhares; um histórico de comandos genérico seria
 * exagero para uma lista de retângulos). Zerar ao trocar de imagem ou fechar
 * o editor é tarefa de quem usa este módulo (um `useEffect` e o desmonte do
 * componente) — aqui só há dados, sem React.
 */

/** Passos mantidos por pilha. Alcançado só em edições muito longas; o mais antigo cai. */
const TETO_PILHA = 50;

export interface HistoricoDeMarcacoes {
  passado: ReviewMask[][];
  futuro: ReviewMask[][];
}

function clonarLista(masks: readonly ReviewMask[]): ReviewMask[] {
  return masks.map((mask) => ({ ...mask }));
}

/** Empilha `item`, mantendo só os `TETO_PILHA` passos mais recentes. */
function empilharComTeto(pilha: readonly ReviewMask[][], item: ReviewMask[]): ReviewMask[][] {
  const proxima = [...pilha, item];
  return proxima.length > TETO_PILHA ? proxima.slice(proxima.length - TETO_PILHA) : proxima;
}

/** Histórico vazio — nenhum desfazer, nenhum refazer disponível. */
export function historicoVazio(): HistoricoDeMarcacoes {
  return { passado: [], futuro: [] };
}

export function podeDesfazer(historico: HistoricoDeMarcacoes): boolean {
  return historico.passado.length > 0;
}

export function podeRefazer(historico: HistoricoDeMarcacoes): boolean {
  return historico.futuro.length > 0;
}

/**
 * Chame IMEDIATAMENTE ANTES de aplicar uma edição nova (desenhar, mover,
 * redimensionar, renomear, reordenar, remover), passando a lista como ela
 * estava antes dessa edição. Empilha em `passado` (teto de `TETO_PILHA`,
 * descartando as entradas mais antigas) e zera `futuro` — uma edição nova
 * sempre invalida o redo.
 */
export function empilhar(historico: HistoricoDeMarcacoes, antes: ReviewMask[]): HistoricoDeMarcacoes {
  return {
    passado: empilharComTeto(historico.passado, clonarLista(antes)),
    futuro: [],
  };
}

/**
 * Desfazer. `atual` é a lista viva (para poder ir para `futuro` e permitir um
 * redo depois). Retorna `null` quando `passado` está vazio — nada para
 * desfazer; quem chama deve não fazer nada nesse caso (o botão já fica
 * desabilitado via `podeDesfazer`, mas um atalho de teclado pode chegar
 * antes). Não toca em `futuro` além de empilhar `atual` — desfazer nunca
 * zera o redo.
 */
export function desfazer(
  historico: HistoricoDeMarcacoes,
  atual: ReviewMask[],
): { historico: HistoricoDeMarcacoes; anterior: ReviewMask[] } | null {
  const { passado } = historico;
  if (passado.length === 0) return null;

  const anterior = passado[passado.length - 1];

  return {
    historico: {
      passado: passado.slice(0, -1),
      futuro: empilharComTeto(historico.futuro, clonarLista(atual)),
    },
    anterior,
  };
}

/**
 * Refazer. Espelho de `desfazer`: tira o topo de `futuro`, empilha `atual`
 * em `passado`. Retorna `null` quando `futuro` está vazio.
 */
export function refazer(
  historico: HistoricoDeMarcacoes,
  atual: ReviewMask[],
): { historico: HistoricoDeMarcacoes; proximo: ReviewMask[] } | null {
  const { futuro } = historico;
  if (futuro.length === 0) return null;

  const proximo = futuro[futuro.length - 1];

  return {
    historico: {
      passado: empilharComTeto(historico.passado, clonarLista(atual)),
      futuro: futuro.slice(0, -1),
    },
    proximo,
  };
}

/**
 * Ctrl/Cmd+Z → 'desfazer'. Ctrl/Cmd+Shift+Z ou Ctrl/Cmd+Y → 'refazer'. Alt
 * pressionado, ou nenhum modificador Ctrl/Cmd, nunca casa (retorna null).
 * Não olha o foco — quem chama confere `focoEmCampoDeTexto` antes.
 */
export function atalhoDeHistorico(event: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}): 'desfazer' | 'refazer' | null {
  if (event.altKey) return null;
  if (!(event.metaKey || event.ctrlKey)) return null;

  const key = event.key.toLowerCase();
  if (key === 'y') return 'refazer';
  if (key === 'z') return event.shiftKey ? 'refazer' : 'desfazer';
  return null;
}
